import React, {useContext, useEffect, useRef, useState} from 'react';
import {assetsApiClient} from "../../../AssetsApiClient";
import {AllOrApprovedProduct, Product} from "../../../models/Product";
import DataEntryHeader from "../../../../components/DataEntry/DataEntryHeader";
import {useHistory, useParams} from "react-router-dom";
import {RouteWithAssetId} from "../../../../routes/types";
import {useAppSelector} from "../../../../store/hooks";
import {selectProfile} from "../../../../ClientManagement/ClientProfile/activeProfileSlice";
import LoadingIndicator from "../../../../pages/LoadingIndicator";
import {StandaloneAccount} from "../../../models/StandaloneAccount";
import {AlertBanner, RequiredFieldsBanner} from "../../../../components";
import {EstateValue, HoldingAssetSubclassDetails, HoldingType} from "../../../models/Holding";
import {isEmpty} from "../../../../utils/stringUtils";
import {EditableHoldingFields} from "../EditHoldingValues";
import HoldingsInfo from "../HoldingsInfo";
import {InvestorGroupType} from "../../../../ClientManagement/models/InvestorGroupType";
import {clientManagementApiClient} from "../../../../ClientManagement/ClientManagementApiClient";
import AddProduct from './AddProduct';
import Holdings from './Holdings';
import {AssetClassifications} from "../../../models/AssetClassifications";
import AssetsViewContext from "../../../common/AssetsViewContext";
import {
    allSelectedAssetClassNames,
    mapHoldingAssetSubclassDetailsToHoldingWriteModel,
    sumByField
} from '../holdingUtils';
import {DropdownOnChangeData} from "../../../../components/Dropdown/Dropdown";
import {HistoryBlockDiscardModal} from "../../../../components/HistoryBlockDiscardModal/HistoryBlockModal";
import deepEquals from "fast-deep-equal";

export default function AddProducts() {
    const history = useHistory();
    const viewType = useContext(AssetsViewContext);
    const {assetId: accountId} = useParams<RouteWithAssetId>();
    const profile = useAppSelector(selectProfile);
    const [standaloneAccount, setStandaloneAccount] = useState<StandaloneAccount>();
    const [initialStandaloneAccount, setInitialStandaloneAccount] = useState<StandaloneAccount>();
    const [initialEditableHoldings, setInitialEditableHoldings] = useState<HoldingAssetSubclassDetails[]>([]);
    const [editableHoldings, setEditableHoldings] = useState<HoldingAssetSubclassDetails[]>([]);
    const [investorGroup, setInvestorGroup] = useState<InvestorGroupType>();
    const [hasInvalidProducts, setHasInvalidProducts] = useState<boolean>(false);
    const [classifications, setClassifications] = useState<AssetClassifications>();
    const [focusOnField, setFocusOnField] = useState<EditableHoldingFields>();
    const [assetClassNamesBySubclassDetailId, setAssetClassNamesBySubclassDetailId] = useState<Map<string, string>>(new Map());
    const [expandedAssetClasses, setExpandedAssetClasses] = useState<string[]>([]);
    const [allOrApprovedProduct, setAllOrApprovedProduct] = useState<AllOrApprovedProduct>("allProducts");
    const [showNavigationModal, setShowNavigationModal] = useState(true);
    const [showBanner, setShowBanner] = useState(true);
    const [focusOnIndex, setFocusOnIndex] = useState<number | undefined>(undefined);
    const marketVal = sumByField(editableHoldings, holding => holding.marketEstateValue.inEstateValue)
    const loans = standaloneAccount?.personalLiabilities.reduce((x, pLiability) => x + pLiability.loanBalance, 0)
    const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);


    useEffect(() => {
        let componentUnmounted = false;
        Promise.all([
            assetsApiClient.getAccount(profile.id, accountId),
            clientManagementApiClient.getInvestorGroup(profile.id),
            assetsApiClient.getAssetClassifications()
        ]).then(([account, investorGroupResponse, assetClassifications]) => {
            if (componentUnmounted) return;
            setStandaloneAccount(account);
            setInitialStandaloneAccount(account);
            setEditableHoldings(account.holdings.allHoldings);
            setInitialEditableHoldings(account.holdings.allHoldings);
            setInvestorGroup(investorGroupResponse);
            setClassifications(assetClassifications);
            setAssetClassNamesBySubclassDetailId(mapSubclassDetailIdsToAssetNames(assetClassifications))
            setExpandedAssetClasses(allSelectedAssetClassNames(assetClassifications, account.holdings.allHoldings));
        }).catch(error => console.error('Could not fetch asset details', error.message));
        return () => {
            componentUnmounted = true;
        }
    }, [accountId]);

    useEffect(() => {
        return () => {
            if (focusTimeoutRef.current) {
                clearTimeout(focusTimeoutRef.current);
            }
        };
    }, []);

    function setFocusWithTimeout(field: EditableHoldingFields, index: number) {
        if (focusTimeoutRef.current) {
            clearTimeout(focusTimeoutRef.current);
        }
        focusTimeoutRef.current = setTimeout(() => {
            setFocusOnField(field);
            setFocusOnIndex(index);
        }, 0);
    }

    function navigateToHoldingSummary() {
        history.push(`/Profile/${profile.id}/ClientProfile/${viewType}/StandaloneAccount/${accountId}/Holdings`);
    }

    const onDiscardClick = () => {
        history.goBack();
    };

    async function onSave() {
        setShowNavigationModal(false)
        if (hasInvalidHoldings(editableHoldings)) {
            setHasInvalidProducts(true);
        } else {
            await assetsApiClient.postHoldings(
                profile.id,
                accountId,
                editableHoldings.map(editableHolding => mapHoldingAssetSubclassDetailsToHoldingWriteModel(editableHolding)));
            navigateToHoldingSummary();
        }
    }

    function hasInvalidHoldings(holdings: HoldingAssetSubclassDetails[]) {
        return holdings.some(holding => !isValidHolding(holding));
    }

    function isValidHolding(holding: HoldingAssetSubclassDetails) {
        return holding.productName && holding.productName.trim().length > 0;
    }

    const handleAllOrApprovedProduct = (allOrApproved: DropdownOnChangeData<AllOrApprovedProduct>) => {
        setAllOrApprovedProduct(allOrApproved.value);

    }

    const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

    function checkIfDuplicate(editableHoldings: HoldingAssetSubclassDetails[], product: Product): number | null {
        setShowDuplicateWarning(false);
        const duplicateIndex = editableHoldings.findIndex(
            holdings => holdings.productId === product.productId && holdings.assetSubclassDetailsId === product.assetSubclassDetailId
        );
        return duplicateIndex !== -1 ? duplicateIndex : null;
    }

    function handleProductSelected(product: Product) {
        if (isEmpty(product.assetSubclassDetailId)) return;

        const duplicateIndex = checkIfDuplicate(editableHoldings, product);

        if (duplicateIndex !== null) {
            setShowDuplicateWarning(true);
            setFocusOnField(undefined);
            setFocusOnIndex(undefined);
            setFocusWithTimeout('taxCost', duplicateIndex);
            return;
        }

        const newIndex = editableHoldings.length;
        setEditableHoldings([
            ...editableHoldings,
            {
                productId: allOrApprovedProduct === "allProducts" ? product.productId : undefined,
                productCode: allOrApprovedProduct === "approvedProducts" ? product.productId : undefined,
                assetSubclassDetailsId: product.assetSubclassDetailId,
                marketValue: null,
                marketEstateValue: {
                    inEstateValue: 0,
                    outOfEstateValue: 0,
                    totalValue: 0
                },
                taxCost: null,
                investableValue: null,
                locked: false,
                concentrated: false,
                assetSubclassDetailsName: product.assetSubclassDetail,
                productName: product.name,
                ticker: product.ticker,
                cusip: product.cusip,
                type: allOrApprovedProduct === "allProducts" ? HoldingType.Product : HoldingType.ApprovedProduct
            }
        ]);
        setFocusWithTimeout('taxCost', newIndex);
        ensureAssetClassExpanded(product.assetSubclassDetailId);
    }

    function handleAddCustomProduct(assetSubclassDetail: HoldingAssetSubclassDetails) {
        if (isEmpty(assetSubclassDetail.assetSubclassDetailsId)) return;

        const newIndex = editableHoldings.length;
        setEditableHoldings([
            ...editableHoldings,
            {
                productId: assetSubclassDetail.productId,
                assetSubclassDetailsId: assetSubclassDetail.assetSubclassDetailsId,
                marketValue: null,
                marketEstateValue: {
                    inEstateValue: 0,
                    outOfEstateValue: 0,
                    totalValue: 0
                },
                taxCost: null,
                investableValue: null,
                locked: false,
                concentrated: false,
                assetSubclassDetailsName: assetSubclassDetail.assetSubclassDetailsName,
                productName: assetSubclassDetail.productName,
                ticker: "",
                cusip: "",
                type: HoldingType.CustomProduct
            }
        ]);
        setFocusWithTimeout('productName', newIndex);
        ensureAssetClassExpanded(assetSubclassDetail.assetSubclassDetailsId);
    }

    function ensureAssetClassExpanded(assetSubclassDetailId: string) {
        const assetClassName = assetClassNamesBySubclassDetailId.get(assetSubclassDetailId);

        if (assetClassName && !expandedAssetClasses.includes(assetClassName)) {
            setExpandedAssetClasses([
                ...expandedAssetClasses,
                assetClassName
            ]);
        }
    }

    function mapSubclassDetailIdsToAssetNames(assetClasses: AssetClassifications) {
        const map = new Map<string, string>();

        assetClasses.riskAssetClasses.concat(assetClasses.riskControlAssetClasses).forEach(assetClass => {
            assetClass.assetSubclasses.forEach(assetSubclass => {
                assetSubclass.assetSubclassDetails.forEach(assetSubclassDetails => {
                    map.set(assetSubclassDetails.assetSubclassDetailsId, assetClass.assetClassName);
                });
            });
        });

        return map;
    }

    function handleDeleteHolding(indexToBeDeleted: number) {
        const updatedHoldings = [
            ...editableHoldings.slice(0, indexToBeDeleted),
            ...editableHoldings.slice(indexToBeDeleted + 1),
        ];

        setEditableHoldings(updatedHoldings);
    }

    function handleUpdateHolding(value: number | string | null | undefined | EstateValue,
                                 index: number,
                                 valueType: EditableHoldingFields) {
        const updatedHoldings = [
            ...editableHoldings.slice(0, index),
            {
                ...editableHoldings[index],
                [valueType]: value,
            },
            ...editableHoldings.slice(index + 1, editableHoldings.length),
        ];

        setEditableHoldings(updatedHoldings);

        if (!hasInvalidHoldings(updatedHoldings)) {
            setHasInvalidProducts(false);
        }
        if (loans! > marketVal) {
            setShowBanner(true);
        }

        if (loans! <= marketVal) {
            setShowBanner(false);
        }
    }

    const handleClose = () => {
        setShowBanner(false)
    }

    if (!standaloneAccount || !investorGroup || !classifications) {
        return <LoadingIndicator/>;
    }

    const inEstateOwnershipPercentage = standaloneAccount.memberOwnerships
        .filter(mo => mo.isInEstateMember)
        .reduce((previous, ownership) => {
            return previous + ownership.percentage;
        }, 0);

    const handleSearch = (searchTerm: string, page?: number) => (
        assetsApiClient.searchProducts(profile.id, searchTerm, page, allOrApprovedProduct)
    );

    const formChanged = () => {
        const updated = {
            ...standaloneAccount,
            ...editableHoldings
        };
        const initial = {
            ...initialStandaloneAccount,
            ...initialEditableHoldings
        }
        return !deepEquals(initial, updated);
    }

    return (
        <div id="searchTypeAheadSection" className="products-container holdings-container">
            <DataEntryHeader
                title={`Edit Products - ${(standaloneAccount?.name)}`}
                primaryButtonText="Save"
                onPrimaryButtonClick={onSave}
                secondaryButtonText="Discard Changes"
                onSecondaryButtonClick={onDiscardClick}
            />
            <RequiredFieldsBanner showAlert={hasInvalidProducts}
                                  message="Product name must be entered to save a Product."/>
            {loans! > marketVal &&
                <AlertBanner
                    className={"liability-holdings-width"}
                    showAlert={showBanner}
                    icon='error'
                    showCloseBtn={true}
                    type='warning'
                    fullWidth={false}
                    onClose={handleClose}
                    message="The value of this asset is lower than its corresponding liabilities."
                />
            }
            <HoldingsInfo account={standaloneAccount}
                          primaryMember={investorGroup.primaryMember}
                          partnerMember={investorGroup.partnerMember}
                          allHoldings={editableHoldings}
                          classifications={classifications}
            />
            {showDuplicateWarning &&
                <AlertBanner
                    className={"liability-holdings-width"}
                    showAlert={true}
                    icon={"info"}
                    type={"warning"}
                    fullWidth={false}
                    showCloseBtn={true}
                    onClose={() => setShowDuplicateWarning(false)}
                    message={"Duplicate products are not allowed: The holding that you selected already exists. Please modify the existing product below."}

                />
            }
            <section className="add-product">
                <AddProduct
                    onPerformSearch={handleSearch}
                    onProductSelect={handleProductSelected}
                    onAddCustomProduct={handleAddCustomProduct}
                    customProductClassifications={classifications}
                    onChangeAllOrApprovedProductDropdown={handleAllOrApprovedProduct}
                    defaultAllOrApprovedProduct={allOrApprovedProduct}/>
            </section>

            <section>
                <Holdings
                    selectedHoldings={editableHoldings}
                    hasInvalidProducts={hasInvalidProducts}
                    isValidHolding={isValidHolding}
                    onUpdateHolding={handleUpdateHolding}
                    assetClassifications={classifications}
                    inEstateOwnershipPercentage={inEstateOwnershipPercentage}
                    focusOnIndex={focusOnIndex}
                    focusOnField={focusOnField}
                    expanded={expandedAssetClasses}
                    onChange={items => setExpandedAssetClasses(items ?? [])}
                    onDeleteHolding={handleDeleteHolding}
                />
            </section>
            <HistoryBlockDiscardModal
                when={formChanged() && showNavigationModal}
            />
        </div>
    )
}
